import { Check, Copy, Loader2, Settings } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useGetBookingLink,
  useGetClass,
  useUpdateTrialEnrollmentMutation,
} from '~entities/classes';
import { useUpdateGHLCalendarLinkMutation } from '~entities/classes/api/update-ghl-calendar-link';

import { useGetGHLCalendars } from '~shared/api/get-ghl-calendars';
import Select from '~shared/components/select/select';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';

interface CalendarOption {
  value: string;
  label: string;
}

interface BookingLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classId: string;
  onEdit: () => void;
}

export const BookingLinkDialog = ({
  open,
  onOpenChange,
  classId,
  onEdit,
}: BookingLinkDialogProps) => {
  const [selectedCalendar, setSelectedCalendar] =
    useState<CalendarOption | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: classData } = useGetClass({ classId });
  const { data, isFetching, error } = useGetBookingLink({
    classId,
  });

  const { data: calendars, isLoading: isLoadingCalendars } =
    useGetGHLCalendars();
  const updateTrialEnrollment = useUpdateTrialEnrollmentMutation();
  const updateGHLCalendarLink = useUpdateGHLCalendarLinkMutation();

  const calendarOptions: Array<CalendarOption> = useMemo(() => {
    if (!calendars || !Array.isArray(calendars)) return [];
    return calendars.map((calendar) => ({
      value: calendar.id,
      label: calendar.name,
    }));
  }, [calendars]);

  const handleConnectCalendar = async () => {
    if (!selectedCalendar) {
      toast.error('Please select a calendar');
      return;
    }
    try {
      await updateGHLCalendarLink.mutateAsync({
        classId,
        ghlCalendarLink: selectedCalendar.value,
      });
      toast.success('Calendar connected', {
        description: 'The booking link is now available.',
      });
    } catch (err) {
      console.error('Failed to connect calendar:', err);
      toast.error('Failed to connect calendar. Please try again.');
    }
  };

  const handleUpdateTrialEnrollment = async () => {
    const enabled = !classData?.allowTrialEnrollment;
    try {
      await updateTrialEnrollment.mutateAsync({
        classId,
        update: {
          enabled,
        },
      });
      toast.success(
        `Trial booking ${enabled ? 'enabled' : 'disabled'} successfully`,
      );
    } catch (err) {
      console.error('Failed to update trial enrollment:', err);
      toast.error(
        `Failed to ${enabled ? 'enable' : 'disable'} trial enrollment`,
      );
    }
  };

  const handleCopy = async () => {
    if (!data?.bookingLink) return;
    try {
      await navigator.clipboard.writeText(data.bookingLink);
      setCopied(true);
      toast.success('Booking link copied.');
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      toast.error('Could not copy the booking link.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className=''>
        <DialogHeader>
          <DialogTitle>Booking Link</DialogTitle>
          <DialogDescription className='flex items-center justify-between'>
            Share this booking link with students or parents
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-4 py-4'>
          {isFetching && (
            <div className='flex justify-center py-4'>
              <Loader2 className='size-6 animate-spin text-primary' />
            </div>
          )}

          {error && (
            <div className='flex flex-col gap-3 rounded-md bg-muted/50 p-3 text-sm'>
              <p className='text-muted-foreground'>
                A calendar needs to be set for this class to generate the
                booking link. Select a calendar below or use Settings → Edit
                Calendar Link.
              </p>
              <div className='flex flex-col gap-2'>
                <label className='text-xs font-medium text-muted-foreground'>
                  Connect class calendar
                </label>
                <div className='flex gap-2'>
                  <div className='min-w-0 flex-1'>
                    <Select
                      value={selectedCalendar}
                      onChange={(option) => {
                        setSelectedCalendar(option);
                      }}
                      options={calendarOptions}
                      placeholder='Select a calendar'
                      isLoading={isLoadingCalendars}
                      isClearable
                    />
                  </div>
                  <Button
                    size='sm'
                    onClick={handleConnectCalendar}
                    disabled={
                      updateGHLCalendarLink.isPending ||
                      isLoadingCalendars ||
                      !selectedCalendar
                    }
                  >
                    {updateGHLCalendarLink.isPending ? (
                      <Loader2 className='size-4 animate-spin' />
                    ) : (
                      'Connect'
                    )}
                  </Button>
                </div>
              </div>
              <Button variant='outline' size='sm' onClick={onEdit}>
                Open calendar settings
              </Button>
            </div>
          )}

          {data && (
            <div className='flex gap-2'>
              <Input
                readOnly
                value={data.bookingLink}
                className='font-mono text-sm'
              />
              <Button
                type='button'
                variant='outline'
                size='icon'
                className='shrink-0'
                aria-label='Copy booking link'
                onClick={() => void handleCopy()}
              >
                {copied ? (
                  <Check className='size-4' />
                ) : (
                  <Copy className='size-4' />
                )}
              </Button>
            </div>
          )}

          <div className='flex items-center gap-2'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' size='sm'>
                  <Settings className='mr-2 size-4' />
                  Settings
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  disabled={updateTrialEnrollment.isPending}
                  onClick={handleUpdateTrialEnrollment}
                >
                  {classData?.allowTrialEnrollment ? 'Disable' : 'Enable'} Trial
                  Enrollment
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onEdit}>
                  Edit Calendar Link
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Close</Button>
          </DialogClose>
          <Button
            onClick={() => window.open(data?.bookingLink, '_blank')}
            disabled={!data?.bookingLink}
          >
            Open in New Tab
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
